import {DataStatus} from 'platform/components';
import {Text} from 'platform/foundation';
import styled from 'styled-components';

import {ChangeEvent} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetTopMakesQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {MakeLogo} from '../../../../../../../components/MakeLogo/MakeLogo';
import {DEFAULT_SOURCING_TARGET_COUNTRY} from '../../../../../../../constants/DEFAULT_SOURCING_TARGET_COUNTRY';
import {useSourcingTargetCountry} from '../../../../../../../hooks/useSourcingTargetCountry';
import {useSourcingTopSelling} from '../../../../../../../hooks/useSourcingTopSelling';
import {useMakeModelFilter} from '../../../../../hooks/useMakeModelFilter';
import {getDisplayedTopMakes} from '../utils/getDisplayedTopMakes';
import {MakeCheckbox} from './MakeCheckbox';

export function TopMakes(props: TestIdProps) {
  const {makes, allSelectedMakes, selectMake, unselectMake} = useMakeModelFilter();
  const {data: preferredMakes, isLoading: isPreferredLoading} = useGetTopMakesQuery();
  const {currentData: topSelling, isFetching: isTopSellingFetching} = useSourcingTopSelling();
  const market = useSourcingTargetCountry();
  const topMakeKeys = getDisplayedTopMakes({
    availableMakes: Object.keys(makes ?? {}),
    preferredMakes: preferredMakes?.map((make) => make.code) ?? [],
    topSelling: topSelling ?? [],
    market: market?.code ?? DEFAULT_SOURCING_TARGET_COUNTRY,
  });
  const topMakes = topMakeKeys.map((key) => makes?.[key]).filter(isNotNil);

  const handleChangeMake = (make: string, isSelected: boolean) => {
    isSelected ? selectMake(make) : unselectMake(make);
  };

  return (
    <Wrapper>
      <DataStatus
        isLoading={isPreferredLoading || (isTopSellingFetching && isNil(topSelling))}
        isEmpty={topMakes?.length === 0}
        emptyMessage={i18n.t('page.filters.labels.noMakes')}
        grow={1}
      >
        {topMakes?.map(({name, label}) => {
          const isSelected = allSelectedMakes.includes(name);

          return (
            <MakeCheckbox
              key={name}
              name={`make.${name}`}
              label={
                <Box title={label}>
                  <MakeLogo
                    make={name}
                    label={label}
                    fallback={<Text size="small">{label}</Text>}
                    data-testid={suffixTestId(`topMake-${name}`, props)}
                  />
                </Box>
              }
              checked={isSelected}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChangeMake(name, e.target.checked)
              }
              data-testid={suffixTestId(`topMake-${name}`, props)}
            />
          );
        })}
      </DataStatus>
    </Wrapper>
  );
}

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 189px;
  margin-bottom: 9px;
  min-height: 50px;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Box = styled.div`
  width: 42px;
  height: 42px;
  background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  border-radius: ${({theme}) => theme.radii.small};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({theme}) => theme.colors.palettes.neutral[20][100]};
  font-size: 10px;
  padding: 3px;
`;
